import type { Row, RowData } from "@tanstack/table-core";
import { VirtualItem, Virtualizer } from "@tanstack/virtual-core";
import type { KeyboardEvent } from "react";
import { cn } from "../../../utils/classes";
import { containerGridStyles } from "../utils/grid";
import { getStickyClasses } from "../utils/sticky";
import { TableBodyCell } from "./cell";

type VirtualTableBodyRowProps<D extends RowData> =
  | {
      rowVirtualizer: Virtualizer<HTMLDivElement, HTMLTableRowElement>;
      virtualRow: VirtualItem;
    }
  | {
      rowVirtualizer?: undefined;
      virtualRow?: undefined;
    };

type TableBodyRowProps<D extends RowData> = {
  columnVirtualizer: Virtualizer<HTMLDivElement, HTMLTableCellElement>;
  row: Row<D>;
  virtualPaddingLeft: number | undefined;
  virtualPaddingRight: number | undefined;
  onRowClick?: (row: D) => void;
  rowClassName?: string;
} & VirtualTableBodyRowProps<D>;

export function TableBodyRow<D extends RowData>({
  columnVirtualizer,
  row,
  rowVirtualizer,
  virtualPaddingLeft,
  virtualPaddingRight,
  virtualRow,
  onRowClick,
  rowClassName,
}: TableBodyRowProps<D>) {
  const visibleCells = row.getVisibleCells();
  const virtualColumns = columnVirtualizer.getVirtualItems();
  const clickable = !!onRowClick;
  return (
    <tr
      {...(virtualRow
        ? {
            "data-index": virtualRow.index, //needed for dynamic row height measurement
            ref: (node) => rowVirtualizer.measureElement(node), //measure dynamic row height
          }
        : {})}
      key={row.id}
      {...(clickable
        ? {
            onClick: () => onRowClick(row.original),
            role: "button" as const,
            tabIndex: 0,
            onKeyDown: (e: KeyboardEvent) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onRowClick(row.original);
              }
            },
          }
        : {})}
      className={cn("items-stretch", rowClassName, {
        "cursor-pointer": clickable,
      })}
      style={{
        ...containerGridStyles,
        width: "100%",
        // TODO: Add more table densities and dynamic heights
        minHeight: "48px",
        // height: "min-content",
        alignItems: "center",
      }}
    >
      {virtualPaddingLeft ? (
        //fake empty column to the left for virtualization scroll padding
        <td
          key="virtual-padding-left"
          style={{
            contentVisibility: "auto",
            containIntrinsicSize: "auto",
            display: "flex",
            width: virtualPaddingLeft,
          }}
        />
      ) : null}
      {virtualColumns.map((vc) => {
        const cell = visibleCells[vc.index];
        return <TableBodyCell key={cell.id} cell={cell} />;
      })}
      {virtualPaddingRight ? (
        //fake empty column to the right for virtualization scroll padding
        <td
          key="virtual-padding-right"
          style={{
            contentVisibility: "auto",
            containIntrinsicSize: "auto",
            display: "flex",
            width: virtualPaddingRight,
          }}
        />
      ) : null}
    </tr>
  );
}
